import URL from 'url-parse';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { linkTypeForUrl } from '../../smartLinks/profile/linkTypeForUrl';
import { isServiceLinkMismatched } from '../../smartLinks/catalog/catalogLinkServices';
import { cleanServiceLinks } from '../../smartLinks/catalog/song/page/draft/songPageDraft';

/**
 * Why a pasted link cannot save (design d5). `otherService`: the address of another service,
 * `album`: an album page, not the song. Null: it can save.
 */
export type PastedLinkProblem = { kind: 'otherService'; service: string } | { kind: 'album' } | null;

function parsedUrl(value: string): URL<Record<string, string | undefined>> | null {
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    return new URL(withScheme, true);
  } catch {
    return null;
  }
}

/**
 * An album page, not a song: a path part `album` or `albums`. An Apple Music song link is an album
 * address with `?i=<song id>`, so that one is a song.
 */
export function isAlbumPageLink(value: string): boolean {
  const url = parsedUrl(value.trim());
  if (url === null) {
    return false;
  }
  const parts = url.pathname.toLowerCase().split('/');
  if (!parts.includes('album') && !parts.includes('albums')) {
    return false;
  }
  return url.query.i === undefined;
}

/** The check while the user types (design d5). An empty field has no problem but cannot save. */
export function pastedLinkProblem(integrationType: string, value: string): PastedLinkProblem {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  if (isServiceLinkMismatched(integrationType, trimmed)) {
    const linkType = linkTypeForUrl(trimmed);
    const other = Object.values(musicServicesByMusicAPIID).find((service) => service.linkType === linkType);
    return { kind: 'otherService', service: other?.name ?? '' };
  }
  return isAlbumPageLink(trimmed) ? { kind: 'album' } : null;
}

/** True when the field can save: not empty and no problem. */
export function canSavePastedLink(integrationType: string, value: string): boolean {
  return value.trim() !== '' && pastedLinkProblem(integrationType, value) === null;
}

/**
 * The full map that `PATCH .../catalog/:uuid { serviceLinks }` takes, with one change: a new
 * address for one service, or `null` to remove it (design d5 "Remove the link"). Null when no
 * link is left.
 */
export function serviceLinksWith(
  current: Record<string, string> | null,
  integrationType: string,
  url: string | null
): Record<string, string> | null {
  const next: Record<string, string> = { ...current };
  if (url === null || url.trim() === '') {
    delete next[integrationType];
  } else {
    next[integrationType] = url.trim();
  }
  return cleanServiceLinks(next);
}

/** The address as the row shows it: no `https://`, no `www.` (design d3 "Linked"). */
export function shownLink(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
}
